/**
 * Central resource-cache accessor.
 *
 * square-admin caches API resource collections (agents, flows, calls, ...) under bare,
 * unnamespaced keys. Historically every consumer read/wrote these keys directly via
 * `localStorage`. This module is the single choke point for that access so that:
 *   - a delegate tab (impersonating a target customer) writes to tab-scoped
 *     `sessionStorage` instead of clobbering the host session's shared `localStorage`.
 *   - every auth-exit path (logout, 401, access-denied, account-frozen, delegate clear)
 *     can reliably wipe every cached resource key via `clearAllResourceCaches()`.
 *
 * See docs/plans/2026-08-09-resource-cache-cleanup-design.md for the full design (VOIP-1316).
 */

/** Authoritative list of resource-collection cache keys. */
export const RESOURCE_CACHE_KEYS = [
  // ProjectAdmin-only
  'customers', 'providers', 'routes',
  // Admin-tier
  'billing_accounts', 'billings', 'numbers',
  // Manager-tier
  'agents', 'campaigns', 'ais', 'conferences', 'conferencecalls', 'extensions',
  'flows', 'outdials', 'queues', 'recordings', 'tags', 'transcribes', 'trunks',
  'webchat_widgets',
  // Common
  'calls', 'conversations', 'messages',
  // Written by resourceCache-adjacent list pages outside LoadResourcesAll
  'aisummaries', 'conversation_accounts', 'emails', 'providercalls', 'teams',
  // Written by ResourceListPage/usePaginatedList(resource=...) — dynamic key sweep,
  // confirmed by `rg 'resource="' src` against every *_list.js.
  'contacts', 'contact_cases', 'contact_addresses', 'aicalls', 'aiaudits',
  'aipromptproposals', 'campaigncalls', 'queuecalls', 'groupcalls', 'activeflows',
  'accesskeys', 'chats', 'rags', 'outplans', 'storage_files', 'mcpservers',
]

const DELEGATE_SESSION_KEY = 'delegate_session'

/**
 * True when this tab is running an unexpired delegate session.
 *
 * NOTE: this duplicates the sessionStorage['delegate_session'] read + expiry check that
 * `loadDelegateFromSession()` in src/store.js performs. It is intentionally NOT imported
 * from store.js to avoid an import cycle (store.js -> provider.js -> resourceCacheStore.js).
 * Keep the key name and expiry semantics in sync with store.js if either changes.
 */
export const isDelegateTab = () => {
  try {
    const raw = sessionStorage.getItem(DELEGATE_SESSION_KEY)
    if (!raw) return false
    const data = JSON.parse(raw)
    if (!data.expire || new Date(data.expire) <= new Date()) return false
    return true
  } catch {
    return false
  }
}

/** sessionStorage for a delegate tab, localStorage otherwise. */
export const getResourceStorage = () => (isDelegateTab() ? sessionStorage : localStorage)

export const readResourceCache = (key) => {
  try {
    return getResourceStorage().getItem(key)
  } catch (e) {
    console.error(`readResourceCache: failed to read '${key}'`, e)
    return null
  }
}

export const writeResourceCache = (key, value) => {
  try {
    getResourceStorage().setItem(key, value)
  } catch (e) {
    console.error(`writeResourceCache: failed to write '${key}'`, e)
  }
}

/**
 * Same fail-tolerant write as `writeResourceCache`, but against an explicitly-provided
 * storage object instead of re-resolving `getResourceStorage()` internally.
 *
 * Callers that issue a long-running request (e.g. an in-flight `Get()` in
 * `provider.js`'s `LoadResource`) should capture `getResourceStorage()` at the moment the
 * request is ISSUED and pass that captured reference here when the response finally
 * resolves. Re-resolving the backend fresh at write time would let a mid-flight
 * session-context change (delegate session cleared, auth cleared) redirect the write to
 * the wrong storage — see VOIP-1316 follow-up.
 */
export const writeResourceCacheTo = (storage, key, value) => {
  try {
    storage.setItem(key, value)
  } catch (e) {
    console.error(`writeResourceCacheTo: failed to write '${key}'`, e)
  }
}

/**
 * Same fail-tolerant read as `readResourceCache`, but against an explicitly-provided
 * storage object instead of re-resolving `getResourceStorage()` internally.
 *
 * Callers that read-modify-write the cache around a long-running request (e.g. create/
 * update/delete handlers that read the existing cached collection, merge in the API
 * response, then write it back) should capture `getResourceStorage()` once, at the moment
 * the request is ISSUED, and use that same captured reference for BOTH the read and the
 * write once the response resolves. Mixing a captured write with a freshly re-resolved
 * read (or vice versa) would let a mid-flight session-context change (delegate session
 * cleared, auth cleared) read from one storage and write to the other — see
 * writeResourceCacheTo() doc comment / VOIP-1316 follow-up.
 */
export const readResourceCacheFrom = (storage, key) => {
  try {
    return storage.getItem(key)
  } catch (e) {
    console.error(`readResourceCacheFrom: failed to read '${key}'`, e)
    return null
  }
}

export const removeResourceCache = (key) => {
  try {
    getResourceStorage().removeItem(key)
  } catch (e) {
    console.error(`removeResourceCache: failed to remove '${key}'`, e)
  }
}

/** Session-only copy of the Assistant Builder conversation (see builderStore.js). */
export const BUILDER_SESSION_KEY = 'builder_session'

/** Session-only copy of the Flow Builder conversation (see views/flows/builder/flowBuilderStore.js). */
export const FLOW_BUILDER_SESSION_KEY = 'flow_builder_session'

// Every builder conversation copy, swept by clearAllResourceCaches().
const BUILDER_SESSION_KEYS = [BUILDER_SESSION_KEY, FLOW_BUILDER_SESSION_KEY]

// Callbacks run by clearAllResourceCaches(). builderStore registers its reset here, because
// this module must not import it (store.js -> provider.js -> resourceCacheStore.js is a cycle).
const clearHooks = []
export const registerCacheClearHook = (fn) => {
  clearHooks.push(fn)
}

/**
 * Remove every registered key. Safe to call repeatedly and from any of the auth-exit
 * call sites (reducers, handleAuthFailure, Login.js, logout handlers) without those call
 * sites needing to know about delegate context — see design doc D7:
 *   - normal (non-delegate) tab: clears the registry from BOTH localStorage and
 *     sessionStorage (sweeps pre-upgrade localStorage residue too).
 *   - active delegate tab (isDelegateTab() true at call time): clears the registry from
 *     sessionStorage ONLY. Never touches localStorage, unconditionally, no exceptions —
 *     localStorage is shared across tabs and a delegate tab must not be able to affect
 *     an unrelated host tab in another window.
 */
export const clearAllResourceCaches = () => {
  const delegate = isDelegateTab()

  RESOURCE_CACHE_KEYS.forEach((key) => {
    try {
      sessionStorage.removeItem(key)
    } catch (e) {
      console.error(`clearAllResourceCaches: failed to remove '${key}' from sessionStorage`, e)
    }

    if (!delegate) {
      try {
        localStorage.removeItem(key)
      } catch (e) {
        console.error(`clearAllResourceCaches: failed to remove '${key}' from localStorage`, e)
      }
    }
  })

  // The builder stores are lazy loaded with ais_create / flows_create, so a logout before they
  // load has no hook. Removing the sessionStorage copies here closes that gap (sessionStorage
  // only, always).
  BUILDER_SESSION_KEYS.forEach((key) => {
    try {
      sessionStorage.removeItem(key)
    } catch (e) {
      console.error(`clearAllResourceCaches: failed to remove '${key}'`, e)
    }
  })

  clearHooks.forEach((fn) => {
    try {
      fn()
    } catch (e) {
      console.error('clearAllResourceCaches: a clear hook threw', e)
    }
  })
}
